/**
 * LiveDataError — the page-level error state for a failed live read.
 *
 * Production never falls back to sample data, so when a live endpoint fails the
 * page says so plainly: a DS `Alert` naming what could not be loaded and the HTTP
 * status. A 503 means the backing store or engine is not configured in this
 * environment, which the `EngineStatusBanner` above the page explains in detail.
 */

import { AlertTriangle, Icon } from "@norican/icons";
import { Alert } from "@norican/react";

import type { ApiError } from "@/lib/api";
import { useTrackOnce } from "@/lib/usage-react";

export interface LiveDataErrorProps {
  /** What failed to load, e.g. "the review queue". */
  what: string;
  /** The query error (an `ApiError` for HTTP failures). */
  error: unknown;
  className?: string;
}

export function LiveDataError({ what, error, className }: LiveDataErrorProps) {
  const status = (error as ApiError | null)?.status;
  // The usage log: a person saw a page that did not load. The status only, no text.
  useTrackOnce("error.page", { status, shown: "page" }, "shown");
  const unconfigured = status === 503 || status === 501;
  return (
    <Alert
      tone={unconfigured ? "warning" : "danger"}
      title={unconfigured ? `Live data not available for ${what}` : `Could not load ${what}`}
      icon={<Icon icon={AlertTriangle} />}
      className={className}
    >
      {unconfigured
        ? "The case store or I2Q is not configured here (HTTP " +
          `${status}). You see nothing rather than sample data.`
        : `The server returned an error${status ? ` (HTTP ${status})` : " (no response)"}. ` +
          "Reload the page to try again."}
    </Alert>
  );
}
